import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Mic, Square } from "lucide-react";
import { de, type ResolvedResult } from "@ainkauf/core";
import { AssistantSheet } from "@/components/AssistantSheet";
import { applyAssistantResult } from "@/db/repository";
import { transkribiere, verstehe } from "@/lib/assistant";
import { starteAufnahme, type AufnahmeSteuerung } from "@/lib/recorder";
import { aufZettel } from "@/lib/assistant-events";
import { verkleinern } from "@/lib/image";
import { supabaseConfigured } from "@/lib/supabase";

type Zustand = "bereit" | "hoert" | "denkt" | "vorschau";

/**
 * Der Mikro-Knopf. Ein Tipp nimmt auf, ein zweiter beendet, zwei Sekunden
 * Stille ebenfalls. Danach kommt die Vorschau, nie ein stilles Anlegen.
 *
 * Bis zum 17.10.2026 schwebte er unten rechts über der Liste und verdeckte
 * die Zeilen darunter. Jetzt sitzt er in der Kopfzeile der jeweiligen Seite,
 * im Einkaufsmodus neben dem Abschluss-Knopf.
 *
 * Daneben liegt ein Knopf für ein Foto: ein abfotografierter Zettel geht
 * denselben Weg, nur ohne Transkription.
 */
export function MicButton({ currentListId, gross = false }: { currentListId?: string | null; /** Im Einkaufsmodus neben dem großen Abschluss-Knopf. */ gross?: boolean }) {
  const [zustand, setZustand] = useState<Zustand>("bereit");
  const [gehoert, setGehoert] = useState("");
  const [ergebnis, setErgebnis] = useState<ResolvedResult | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [pegel, setPegel] = useState(0);
  const steuerung = useRef<AufnahmeSteuerung | null>(null);
  const pegelTimer = useRef<number | null>(null);

  // Ein Fehler oben am Rand soll nicht stehen bleiben, bis jemand wieder
  // aufnimmt. Fünf Sekunden reichen zum Lesen.
  useEffect(() => {
    if (fehler === null) return;
    const uhr = window.setTimeout(() => {
      setFehler(null);
    }, 5000);
    return () => {
      window.clearTimeout(uhr);
    };
  }, [fehler]);

  const aufraeumen = useCallback(() => {
    if (pegelTimer.current !== null) window.clearInterval(pegelTimer.current);
    pegelTimer.current = null;
    steuerung.current = null;
    setPegel(0);
  }, []);

  const verarbeite = useCallback(
    async (eingabe: { text?: string; image?: string }) => {
      setZustand("denkt");
      try {
        const verstanden = await verstehe({ ...eingabe, currentListId });
        setErgebnis(verstanden);
        setZustand("vorschau");
      } catch (error) {
        setFehler(error instanceof Error ? error.message : de.assistant.failed);
        setZustand("bereit");
      }
    },
    [currentListId],
  );

  // Ein Zettel-Foto aus der Schnelleingabe geht denselben Weg wie Sprache,
  // nur ohne Transkription.
  useEffect(
    () =>
      aufZettel((datei) => {
        void verkleinern(datei).then((blob) => {
          const leser = new FileReader();
          leser.onload = () => {
            setGehoert(de.assistant.photoNote);
            void verarbeite({ image: String(leser.result) });
          };
          leser.readAsDataURL(blob);
        });
      }),
    [verarbeite],
  );

  const starte = useCallback(async () => {
    setFehler(null);
    if (!navigator.onLine) {
      setFehler(de.assistant.offline);
      return;
    }

    try {
      const gestartet = await starteAufnahme((aufnahme) => {
        aufraeumen();
        if (!aufnahme) {
          setZustand("bereit");
          return;
        }
        setZustand("denkt");
        void transkribiere(aufnahme.blob)
          .then(async (text) => {
            setGehoert(text);
            if (text.trim().length === 0) {
              setFehler(de.assistant.nothingUnderstood);
              setZustand("bereit");
              return;
            }
            await verarbeite({ text });
          })
          .catch((error: unknown) => {
            setFehler(error instanceof Error ? error.message : de.assistant.failed);
            setZustand("bereit");
          });
      });

      steuerung.current = gestartet;
      setZustand("hoert");
      pegelTimer.current = window.setInterval(() => {
        setPegel(gestartet.pegel());
      }, 100);
    } catch {
      setFehler(de.assistant.failed);
      setZustand("bereit");
    }
  }, [aufraeumen, verarbeite]);

  const uebernehmen = useCallback(async () => {
    if (!ergebnis) return;
    await applyAssistantResult(ergebnis);
    setErgebnis(null);
    setGehoert("");
    setZustand("bereit");
  }, [ergebnis]);

  if (!supabaseConfigured()) return null;

  const beschriftung = zustand === "hoert" ? de.assistant.stop : de.assistant.start;
  // Der Pegel wächst als Ring um den Knopf, als Schatten statt als eigenes
  // Element: So braucht der Knopf keinen Platz drumherum und passt in jede
  // Zeile.
  const ring = zustand === "hoert" ? 3 + Math.min(pegel * 60, 9) : 0;

  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (zustand === "hoert") steuerung.current?.stop();
          else if (zustand === "bereit") void starte();
        }}
        disabled={zustand === "denkt"}
        aria-label={beschriftung}
        data-testid="mic-button"
        data-state={zustand}
        className={`inline-flex shrink-0 items-center justify-center rounded-full transition-all duration-100 active:scale-90 ${
          gross ? "size-[60px] shadow-[var(--shadow-float)]" : "size-11"
        } ${zustand === "hoert" ? "bg-ink text-white" : gross ? "bg-card text-accent" : "bg-accent-soft text-accent"}`}
        style={{ boxShadow: ring > 0 ? `0 0 0 ${String(ring)}px color-mix(in oklab, var(--accent) 25%, transparent)` : undefined }}
      >
        {zustand === "denkt" ? (
          <Loader2 size={gross ? 24 : 20} strokeWidth={2} className="animate-spin" aria-hidden />
        ) : zustand === "hoert" ? (
          <Square size={gross ? 20 : 16} strokeWidth={2} aria-hidden />
        ) : (
          <Mic size={gross ? 24 : 20} strokeWidth={2} aria-hidden />
        )}
      </button>

      {/* Zustand und Fehler oben am Rand, wo der Blick nach dem Tipp ohnehin
          ist. Früher standen sie über dem schwebenden Knopf unten rechts. */}
      {(fehler !== null || zustand === "hoert" || zustand === "denkt") && (
        <div
          className="pointer-events-none fixed inset-x-0 z-40 flex justify-center px-4"
          style={{ top: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
        >
          {fehler !== null ? (
            <p role="alert" data-testid="assistant-error" className="max-w-[90vw] rounded-[var(--radius-md)] bg-card px-3 py-2 text-sm text-accent shadow-[var(--shadow-card)]">
              {fehler}
            </p>
          ) : (
            <span className="rounded-full bg-card px-3 py-1 text-sm font-semibold text-ink-2 shadow-[var(--shadow-card)]">
              {zustand === "hoert" ? de.assistant.listening : de.assistant.thinking}
            </span>
          )}
        </div>
      )}

      {zustand === "vorschau" && ergebnis !== null && (
        <AssistantSheet
          gehoert={gehoert}
          ergebnis={ergebnis}
          onApply={() => void uebernehmen()}
          onClose={() => {
            setErgebnis(null);
            setZustand("bereit");
          }}
          onReplace={(aktion, artikel, name) => {
            setErgebnis((alt) => {
              if (!alt) return alt;
              const actions = alt.actions.map((a, i) =>
                i !== aktion
                  ? a
                  : { ...a, items: a.items.map((item, j) => (j === artikel ? { ...item, name, needsClarification: false } : item)) },
              );
              return { ...alt, actions };
            });
          }}
        />
      )}
    </>
  );
}
